import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import type { TableProps } from 'antd';
import { Button, Select, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMemo } from 'react';
import type {
	ExportOrderDto,
	ExportOrderSapDto,
	OrderReason,
} from '../../../../api-client';

type BVOrdersExportTableProps = {
	loading: boolean;
	orderReasons: OrderReason[];
	selectedReasonCode?: string;
	setOrderReasonCode: (code?: string) => void;
	orders: ExportOrderDto[];
	onRefresh: () => void | Promise<void>;
	loadingExport: boolean;
	onExport: () => void | Promise<void>;
};

const BVOrdersExportTable = ({
	loading,
	orderReasons,
	selectedReasonCode,
	setOrderReasonCode,
	orders,
	onRefresh,
	onExport,
	loadingExport,
}: BVOrdersExportTableProps) => {
	const reasonOptions = useMemo(
		() =>
			(orderReasons ?? []).map((r) => ({
				label: r.description ?? '',
				value: r.code ?? '',
			})),
		[orderReasons],
	);

	const columns: ColumnsType<ExportOrderDto> = useMemo(
		() => [
			{
				title: 'Customer',
				dataIndex: 'fullName',
				key: 'fullName',
				render: (_v, r) => (
					<div style={{ display: 'flex', flexDirection: 'column' }}>
						<span>{r.fullName || '-'}</span>
						<span style={{ opacity: 0.7, fontSize: 12 }}>
							{r.idCustomerSapCode || '-'}
						</span>
					</div>
				),
			},
			{
				title: 'PO Number',
				dataIndex: 'poNumber',
				key: 'poNumber',
				width: 160,
				render: (v?: string) => v || '-',
			},
			{
				title: 'Created At',
				dataIndex: 'createdAt',
				key: 'createdAt',
				width: 180,
				render: (v?: string) => (v ? new Date(v).toLocaleString() : '-'),
			},
			{
				title: 'Qty',
				dataIndex: 'quantity',
				key: 'quantity',
				width: 90,
				align: 'right',
				render: (v?: number) => v ?? 0,
			},
			{
				title: 'Net',
				dataIndex: 'priceNetBillingTotal',
				key: 'priceNetBillingTotal',
				width: 140,
				align: 'right',
				render: (_v, r) =>
					`${(r.priceNetBillingTotal ?? 0).toLocaleString()} ${r.currencyPriceNet ?? ''}`.trim(),
			},
			{
				title: 'SAP Orders',
				dataIndex: 'ordersSap',
				key: 'ordersSapCount',
				width: 120,
				align: 'right',
				render: (v?: ExportOrderSapDto[]) => v?.length ?? 0,
			},
			{
				title: 'Note',
				dataIndex: 'note',
				key: 'note',
				render: (v?: string) => v || '-',
			},
		],
		[],
	);

	const sapColumns: ColumnsType<ExportOrderSapDto> = useMemo(
		() => [
			{
				title: 'SAP Code',
				dataIndex: 'orderSapCode',
				key: 'orderSapCode',
				width: 150,
				render: (v?: string) => v || '-',
			},
			{
				title: 'Status',
				dataIndex: 'statusOrder',
				key: 'statusOrder',
				width: 130,
				render: (v?: string) => (v ? <Tag>{v}</Tag> : '-'),
			},
			{
				title: 'Business Unit',
				dataIndex: 'businessUnitCode',
				key: 'businessUnitCode',
				width: 120,
				render: (v?: string) =>
					v === 'W' ? (
						<Tag color="blue">W</Tag>
					) : v === 'J' ? (
						<Tag color="purple">J</Tag>
					) : (
						v || '-'
					),
			},
			{
				title: 'Ship To',
				key: 'shipTo',
				render: (_v, r) => (
					<div style={{ display: 'flex', flexDirection: 'column' }}>
						<span>{r.name || '-'}</span>
						<span style={{ opacity: 0.7, fontSize: 12 }}>
							{[r.address, r.city, r.zipCode, r.country]
								.filter(Boolean)
								.join(', ') || '-'}
						</span>
					</div>
				),
			},
			{
				title: 'Qty',
				dataIndex: 'quantity',
				key: 'quantity',
				width: 90,
				align: 'right',
				render: (v?: number) => v ?? 0,
			},
			{
				title: 'Net',
				dataIndex: 'priceNetBilling',
				key: 'priceNetBilling',
				width: 140,
				align: 'right',
				render: (_v, r) =>
					`${(r.priceNetBilling ?? 0).toLocaleString()} ${r.currencyPriceNetBilling ?? ''}`.trim(),
			},
			{
				title: 'Message',
				dataIndex: 'sapOrderMessage',
				key: 'sapOrderMessage',
				render: (v?: string) => v || '-',
			},
		],
		[],
	);

	const expandedRowRender = (record: ExportOrderDto) => {
		const sap = record.ordersSap ?? [];
		return (
			<Table<ExportOrderSapDto>
				rowKey={(r) => r.orderSapCode ?? `${r.name}-${r.city}`}
				columns={sapColumns}
				dataSource={sap}
				pagination={false}
				size="small"
			/>
		);
	};

	const handleTableChange: TableProps<ExportOrderDto>['onChange'] = () => {
		// no-op: no local filters in this table for now
	};

	return (
		<div className="admin-card" style={{ marginTop: 16 }}>
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					gap: 12,
					flexWrap: 'wrap',
				}}
			>
				<label className="admin-label">Orders</label>

				<div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
					<Select
						style={{ minWidth: 320 }}
						placeholder="Select reason code"
						options={reasonOptions}
						value={selectedReasonCode}
						allowClear
						showSearch
						optionFilterProp="label"
						onChange={(value) => setOrderReasonCode(value)}
					/>

					<Button
						icon={<ReloadOutlined />}
						onClick={onRefresh}
						loading={loading}
					>
						Refresh
					</Button>
					<Button
						icon={<DownloadOutlined />}
						onClick={onExport}
						loading={loadingExport}
					>
						Download
					</Button>
				</div>
			</div>

			<Table<ExportOrderDto>
				style={{ marginTop: 12 }}
				rowKey={(r) =>
					// prefer a real id if you have one; fallback to a composite
					`${r.idCustomerSapCode}-${r.poNumber}-${r.createdAt}`
				}
				loading={loading}
				dataSource={orders}
				columns={columns}
				pagination={{ pageSize: 20 }}
				onChange={handleTableChange}
				expandable={{
					expandedRowRender,
					rowExpandable: (r) => (r.ordersSap?.length ?? 0) > 0,
				}}
			/>
		</div>
	);
};

export default BVOrdersExportTable;
